import React from 'react';
import { Mui } from '@osu/react-ui';
import { 
  LoadingSkeleton, 
  PullToRefresh 
} from './ui';
import { 
  DashboardContent, 
  DashboardError 
} from './dashboard';


/**
 * Authentication Loading Component
 * Displays while authentication is being verified
 */
const AuthenticationLoading = () => (
  <Mui.Container maxWidth="lg" sx={{ py: 4 }}>
    <Mui.Typography
      variant="h1"
      component="h1"
      gutterBottom
      sx={{ fontSize: '2.125rem' }}
      id="main-content"
    >
      Student Dashboard
    </Mui.Typography>

    <Mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 4 }}>
      <Mui.CircularProgress size={24} sx={{ mr: 2 }} />
      <Mui.Typography variant="body1">
        Verifying your access...
      </Mui.Typography>
    </Mui.Box>

    {/* Loading announcement for screen readers */}
    <div
      aria-live="polite"
      aria-atomic="true"
      role="status"
      style={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden'
      }}
    >
      Verifying your access to the dashboard, please wait...
    </div>
  </Mui.Container>
);

/**
 * Main renderer component that handles all conditional rendering logic
 * for the Student Dashboard based on authentication, loading, and data states
 */
function StudentDashboardRenderer({
  // Authentication state
  isAuthenticated,
  authLoading,
  
  // Data state
  applicationData,
  processedApplicationData,
  isLoading,
  isError,
  error,
  
  // Timeline and navigation
  timelineSteps,
  stepNavigation,
  
  // Actions
  onStepAction,
  onRefresh,
  
  // Admin and debug
  isAdminOverride,
  roles,
  debugPanel
}) {
  // Authentication checks - skip if in admin mode
  if (!isAuthenticated && !authLoading) {
    return <AuthenticationLoading />;
  }

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (isError) {
    return <DashboardError error={error} onRetry={onRefresh} />;
  }

  // No data state
  if (!applicationData) {
    return (
      <Mui.Container maxWidth="lg" sx={{ py: 4 }}>
        <Mui.Alert severity="info">
          <Mui.AlertTitle>No Application Data</Mui.AlertTitle>
          No application data found. Please check back later.
        </Mui.Alert>
      </Mui.Container>
    );
  }

  // Success state - render main dashboard content
  return (
    <>
      {/* Main Content with Pull-to-Refresh */}
      <main
        id="application-status"
        role="main"
        style={{ minHeight: '400px' }}
      >
        <PullToRefresh
          onRefresh={onRefresh}
          enabled={!isAdminOverride} // Disable for admin view
        >
          <DashboardContent
            applicationData={processedApplicationData}
            timelineSteps={timelineSteps}
            stepNavigation={stepNavigation}
            onStepAction={onStepAction}
          />
        </PullToRefresh>
      </main>

      {/* Debug panel - only show in development or for admin users */}
      {debugPanel && (
        debugPanel
      )}
    </>
  );
}

export default StudentDashboardRenderer;